// src/context/ApiProvider.tsx
import axios, { AxiosInstance } from 'axios';
import { createContext, useContext, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthProvider';

const ApiContext = createContext<AxiosInstance | null>(null);

/* Read once from CRA env and normalize */
const RAW_API_BASE = (process.env.REACT_APP_API_BASE ?? '').trim();
const API_BASE = RAW_API_BASE.replace(/\/+$/, ''); // drop any trailing slash

export const ApiProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_BASE ? `${API_BASE}/api` : '/api',
      withCredentials: true,
    });

    /* Attach an ID token on every request (Firebase handles refresh when needed) */
    instance.interceptors.request.use(async cfg => {
      if (user) {
        const token = await user.getIdToken();
        cfg.headers.Authorization = `Bearer ${token}`;
      }
      return cfg;
    });

    const isBannedResponse = (status: number | undefined, payload: any): boolean => {
      if (status === 423) return true;
      if (status !== 403) return false;
      const probe = [
        payload?.code,
        payload?.error,
        payload?.message,
        payload?.details,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return (
        probe.includes('banned') ||
        probe.includes('locked') ||
        probe.includes('disabled') ||
        probe.includes('account')
      );
    };

    /* Optional: redirect to landing on 401s */
    instance.interceptors.response.use(undefined, err => {
      const status = err?.response?.status as number | undefined;
      if (status === 401) window.location.href = '/';
      if (isBannedResponse(status, err?.response?.data)) {
        window.location.href = '/account-locked';
      }
      return Promise.reject(err);
    });

    return instance;
  }, [user]);

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
};

export const useApi = () => {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('useApi must be inside <ApiProvider>');
  return ctx;
};
